import { Injectable } from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
  DynTableColAlign,
  DynTableColType,
  DynTableColumn,
  DynTableColWidth,
  DynTableRowSpan,
  DynTableSearchType
} from './dyn-table.model';
import * as moment from 'moment';
import { Moment } from 'moment';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

@Injectable({ providedIn: 'root' })
export class DynTableService {
  constructor(private deviceService: DeviceDetectorService,
              public sanitizer: DomSanitizer) {
  }

  static PRIMARY_COLOR = '#040303';
  static DATE_FORMAT_JSON = 'yyyy-MM-ddTHH:mm:ssZ'; // formato json OK per backend
  // static DATE_FORMAT_TABLE = 'dd/MM/yyyy'; // formato di visualizzazione date per frontend
  static DATETIME_FORMAT_TABLE = 'dd/MM/yyyy hh:mm'; // formato di visualizzazione date/tempo per frontend

  /**
   * Utility per larghezze colonne dyn-table
   */
  public getColWidth(type: DynTableColWidth, customWidth?: number) {
    switch (type) {
      case DynTableColWidth.MINI:
        return { width: this.deviceService.isDesktop() ? '3rem' : '4rem' };
      case DynTableColWidth.VERY_SMALL:
        return { width: this.deviceService.isDesktop() ? '4.5rem' : '5.5rem' };
      case DynTableColWidth.SMALL:
        return { width: this.deviceService.isDesktop() ? '6rem' : '7rem' };
      case DynTableColWidth.MEDIUM:
        return { width: this.deviceService.isDesktop() ? '8rem' : '9rem' };
      case DynTableColWidth.LARGE:
        return { width: this.deviceService.isDesktop() ? '12rem' : '13rem' };
      case DynTableColWidth.VERY_LARGE:
        return { width: this.deviceService.isDesktop() ? '14rem' : '15rem' };
      case DynTableColWidth.HUGE:
        return { width: this.deviceService.isDesktop() ? '17rem' : '18rem' };
      case DynTableColWidth.MAX:
        return { width: this.deviceService.isDesktop() ? '20rem' : '21rem' };
      case DynTableColWidth.OVERMAX:
        return { width: this.deviceService.isDesktop() ? '30rem' : '31rem' };
      case DynTableColWidth.DATE_WIDTH:
        return { width: this.deviceService.isDesktop() ? '6rem' : '7rem' };
      case DynTableColWidth.NDG_WIDTH:
        return { width: this.deviceService.isDesktop ? '7rem' : '7rem' };
      case DynTableColWidth.CUSTOM:
        // dimensione "custom"
        return { width: this.deviceService.isDesktop ? customWidth + 'rem' : customWidth + 1 + 'rem' };
      default:
        // di default width "MEDIUM"
        return { width: this.deviceService.isDesktop() ? '8rem' : '9rem' };
    }
  }

  /**
   * Utility per allineamenti content colonne dyn-table (N.B.: di default l'allineamento è center, usare solo quando serve realmente..)
   */
  public getColAlign(type: DynTableColAlign): any {
    switch (type) {
      case DynTableColAlign.LEFT:
        return { 'text-align': 'left' };
      case DynTableColAlign.RIGHT:
        return { 'text-align': 'right' };
      case DynTableColAlign.CENTER:
        return { 'text-align': 'center' };
    }
  }

  /**
   * Formattazione degli importi in tabella
   */
  public formatAmount(amount, useDecimal: boolean) {
    if (!amount) {
      return useDecimal ? '0,00' : '0';
    }
    if (!useDecimal) {
      amount
        .toString()
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')
        .replace(',', '.');
    }
    if (useDecimal) {
      amount = Number(amount);
      amount = amount.toFixed(2).toString().replace('.', ',');
      return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    } else {
      return amount;
    }
  }

  public padString(n, width, padString = '0') {
    n = n + '';
    return n.length >= width ? n : new Array(width - n.length + 1).join(padString) + n;
  }

  /**
   * Conversione di date
   */
  public convertTimezone(timezone, formatHHmmss = false) {
    if (!timezone) {
      return null;
    }
    if (timezone.length === 16) {
      timezone = timezone.substr(0, 10) + 'T00:00:00' + timezone.substr(10);
    } else if (timezone.length === 22) {
      timezone = timezone.substr(0, 16) + ':00' + timezone.substr(16);
    } else if (timezone.length >= 29 && timezone.length <= 32) {
      timezone = timezone.replace(/\.\d+/, '');
    } else if (timezone.length !== 25) {
      return null;
    }
    const date = new Date(Date.parse(timezone));
    return this.formatDate(date, formatHHmmss);
  }

  private formatDate(date, formatHHmmss: boolean, formatTrattini?: boolean, formatHHmm?: boolean) {
    // Hours part from the timestamp
    const hours = date.getHours();
    // Minutes part from the timestamp
    const minutes = '0' + date.getMinutes();
    // Seconds part from the timestamp
    const seconds = '0' + date.getSeconds();

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    if (formatTrattini) {
      return this.padString(year, 4) + '-' + this.padString(month, 2) + '-' + this.padString(day, 2);
    }
    // Will display time in 10:30:23 format
    if (formatHHmm) {
      return (
        this.padString(day, 2) +
        '/' +
        this.padString(month, 2) +
        '/' +
        year +
        ' ' +
        this.padString(hours, 2) +
        ':' +
        this.padString(minutes.substr(-2), 2)
      );
    }
    if (formatHHmmss) {
      return (
        this.padString(day, 2) +
        '/' +
        this.padString(month, 2) +
        '/' +
        year +
        ' ' +
        hours +
        ':' +
        minutes.substr(-2) +
        ':' +
        seconds.substr(-2)
      );
    } else {
      return this.padString(day, 2) + '/' + this.padString(month, 2) + '/' + year;
    }
  }

  convertToTableFormatDateArray(dateArray: string[]): string {
    if (dateArray && dateArray.length > 0) {
      return dateArray.map(d => this.convertToTableFormatDate(d, 'DD/MM/YYYY')).toString();
    }
    return null;
  }

  convertToTableFormatDate(date: string, format?: string): string {
    return date ? moment(date).format(format ? format : 'DD/MM/YYYY HH:mm') : null;
  }

  public convertDateToMoment(date: Date): Moment {
    // per filtri di tipo DATA "secca" bisogna convertire l'utc (+2 ore)
    return moment.utc({ day: date.getDate(), month: date.getMonth(), year: date.getFullYear() });
  }

  public convertTimestampToMoment(date: Date): Moment {
    return moment({
      day: date.getDate(), month: date.getMonth(), year: date.getFullYear(),
      hours: date.getHours(), minutes: date.getMinutes(), seconds: date.getSeconds()
    });
  }

  public convertTimestampToUtcMoment(date: Date): Moment {
    return moment({
      day: date.getDate(), month: date.getMonth(), year: date.getFullYear(),
      hours: date.getHours(), minutes: date.getMinutes(), seconds: date.getSeconds()
    });
  }

  /**
   * N.B.!!: Presume che la data passata sia in formato "JSON" --> 'yyyy-MM-ddTHH:mm:ssZ'
   */
  public convertStringToDate(dateString: string): Date {
    if (dateString) {
      const aaaammgg: string[] = dateString.split('T')[0].split('-');
      const hhmmss: string[] = dateString.split('T')[1].split(':');
      hhmmss[2] = hhmmss[2].replace('Z', '');
      return new Date(Number(aaaammgg[0]), Number(aaaammgg[1]) - 1, Number(aaaammgg[2]),
        Number(hhmmss[0]), Number(hhmmss[1]), Number(hhmmss[2]));
    }
    return null;
  }

  /**
   * Per generare la configurazione di un eventuale rowspan dyn-table "standard"
   * @param items - dati righe dyntable
   * @param rowspanFieldName - nome del campo da cui prelevare la chiave di aggregazione
   * @param header - traduzione header della colonna rowspan
   * @param style - stile header/colonna rowspan (tipicamente "width")
   */
  public createRowspanMetadata(items: any[], rowspanFieldName: string, header: string, style: any): DynTableRowSpan {
    const rowSpanMetadata: DynTableRowSpan = { header, style };
    if (items && rowspanFieldName) {
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        const keyField = item[rowspanFieldName]; // VALORE del campo indicato come chiave... sarà il valore dentro la cella rowspan
        if (i === 0) {
          rowSpanMetadata[keyField] = { index: 0, size: 1, value: keyField };
        } else {
          const previousRowData = items[i - 1];
          const previousRowGroup = previousRowData.anagrafica;
          if (keyField === previousRowGroup) {
            // stessa chiave --> incremento size
            rowSpanMetadata[keyField].size++;
          } else {
            // chiave diversa --> cambio index e size ricomincia da 1
            rowSpanMetadata[keyField] = { index: i, size: 1, value: keyField };
          }
        }
      }
    }
    return rowSpanMetadata;
  }

  /**
   * renderizza la colonna in base al tipo colonna configurato in ogni tabella
   */
  renderField(field: DynTableColumn, value: any): SafeHtml {
    const type = field.type;
    let html = '';
    if (!type) {
      return value;
    }
    if (type === DynTableColType.BOOLEAN) {
      if (value) {
        html = '<i class="fas fa-check" style="color: #28a745; font-size: x-large;"></i>';
      } else {
        html = '<i class="fas fa-times" style="color: #dc3545; font-size: x-large;"></i>';
      }
    }
    if (type === DynTableColType.DOTS) {
      if (value != null && value === true) {
        html = '<i class="fas fa-circle" style="color: #28a745; font-size: x-large;"></i>';
      } else if (value != null && value === false) {
        html = '<i class="fas fa-circle" style="color: #dc3545; font-size: x-large;"></i>';
      } else {
        html = value;
      }
    }
    if (type === DynTableColType.ICON) {
      html = '<i class="' + value + '" style="color: ' + DynTableService.PRIMARY_COLOR + '; font-size: x-large;"></i>';
    }
    if (type === DynTableColType.ICON_ACTION && value) {
      html = '<a class="' + value.split(';')[0] + '" '
        + '(click)="iconActionClicked.emit({row: rowData, actionType: ' + value.split(';')[1] + '})"></a>';
    }
    if (type === DynTableColType.BADGE) {
      html = value ? '<span class="' + value.split(';')[0] + '" style="font-size: 0.8rem;">' + value.split(';')[1] + '</span>' : '';
    }
    if (type === DynTableColType.IMAGE) {
      html = '<img src="' + value + '">';
    }
    if (type === DynTableColType.URL) {
      html = '<a href=\'%%\'>%%</a>'.replace(/%%/g, value);
    }
    if (type === DynTableColType.EURO) {
      html = this.formatAmount(value, true) + ' €';
    }
    if (type === DynTableColType.EURO_NO_DECIMAL) {
      html = this.formatAmount(value, false);
    }
    if (type === DynTableColType.MOMENT_DATE) {
      const converted: string = this.convertToTableFormatDate(value);
      html = converted ? converted.split(' ')[0] : null; // data --> solo 1^ parte
    }
    if (type === DynTableColType.MOMENT_TS) {
      html = this.convertToTableFormatDate(value); // ts --> lascio anche l'orario
    }
    if (type === DynTableColType.MOMENT_DATE_ARRAY) {
      html = this.convertToTableFormatDateArray(value);
    }
    if (type === DynTableColType.DATETIME) {
      html = value ? this.convertTimezone(value, true) : '-';
    }
    if (type === DynTableColType.CUSTOM) {
      html = value;
    }
    if (type === DynTableColType.ROUTE) {
      html = '<div class="link-like">' + (value != null ? value : '') + '</div>';
    }
    if (type === DynTableColType.DOWNLOAD) {
      html = '<div class="link-like"><li>' + (value != null ? value : '') + '</li></div>';
    }
    if (type === DynTableColType.ICON_BADGE && value) {
      // se contiene il ';' significa che è un badge
      if (value.indexOf(';') !== -1) {
        html = '<span class="' + value.split(';')[0] + '" style="font-size: 0.8rem;">' + value.split(';')[1] + '</span>';
      } else {
        html =
          '<i class="' + value.split('--')[0] + '" style="color: ' + DynTableService.PRIMARY_COLOR + '; font-size: x-large;" title="'
          + value.split('--')[1] + '"></i>';
      }
    }
    if (type === DynTableColType.INFO_GROUP && value) {
      // in questo caso value sarà un array di stringhe
      html = '<div class="btn-group">';
      value.forEach(v => {
        html += ' <button type="button" class="btn btn-' + (v !== '-' ? 'success' : 'info') + ' btn-sm" disabled style="width: 45px">'
          + v + '</button>';
      });
      html += '</div>';
    }
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

}
